import { Card } from 'primereact/card'
import { DataTable } from 'primereact/datatable'
import { Column as TableColumn } from 'primereact/column'

import { PermissionSuffix } from '../../_generated/api/user-management'
import { useDeviceContext } from '../contexts/DeviceContext'
import { useUserContext } from '../contexts/UserContext'
import { ContentColumn, LayoutColumnContainer, LayoutRowContainer } from '../common/pure/layout'
import ContextHeader from '../common/ContextHeader'
import DateDisplay from '../common/pure/DateDisplay'
import Heading from '../common/pure/Heading'
import TablePagination from '../table/TablePagination'
import { ArchiveButton, RestoreButton, TableActionContainer } from '../table/TableButton'
import {
    getFilterableDateRangeColumnProps,
    getSortableColumnProps,
    getSortableTableProps,
} from '../table/table-props'
import Device from '../../models/Device'
import t from '../../tools/t'

export default function OrganizationDevicesScreen() {
    const { organizationDevices, archiveDevice, restoreDevice } = useDeviceContext()
    const { me } = useUserContext()
    const canWriteHardware = !!me?.hasAdminPermission(PermissionSuffix.HardwareWrite)

    const onArchiveDevice = async (device: Device) => {
        if (!confirm('Are you sure you want to archive device:\n' + device.serialNo)) return
        await archiveDevice(device.id)
    }

    return (
        <LayoutColumnContainer>
            <LayoutRowContainer>
                <ContentColumn columns={3}>
                    <ContextHeader>
                        <Heading standalone size={2}>
                            {t('common_devices')}
                        </Heading>
                    </ContextHeader>
                    <Card>
                        <DataTable
                            value={organizationDevices.items}
                            rowClassName={(d: Device) =>
                                d.isArchived ? 'archived-table-row' : undefined
                            }
                            rowHover={true}
                            emptyMessage={t('hardware_empty')}
                            {...getSortableTableProps(organizationDevices)}
                        >
                            <TableColumn
                                header={t('device_field_type')}
                                body={(d: Device) => d.type}
                            />

                            <TableColumn
                                header={t('device_field_serial')}
                                body={(d: Device) => d.serialNo}
                            />

                            <TableColumn
                                header={t('device_field_software_version')}
                                body={(d: Device) => d.softwareVersion}
                            />

                            <TableColumn
                                header={t('device_field_hardware_version')}
                                body={(d: Device) => d.hardwareVersion}
                            />

                            <TableColumn
                                header={t('device_field_multimax_serial')}
                                body={(d: Device) => d.multiMaxSerialNo}
                            />

                            <TableColumn
                                header={t('life_cycle_field_created_at')}
                                body={(d: Device) => (
                                    <DateDisplay value={d.createdAt} target={d.id} />
                                )}
                                {...getSortableColumnProps('createdAt')}
                                {...getFilterableDateRangeColumnProps(
                                    'createdAt',
                                    organizationDevices
                                )}
                            />

                            {canWriteHardware && (
                                <TableColumn
                                    body={(d: Device) => (
                                        <TableActionContainer>
                                            {d.isArchived ? (
                                                <RestoreButton
                                                    target={d.id}
                                                    onClick={() => restoreDevice(d.id)}
                                                />
                                            ) : (
                                                <ArchiveButton
                                                    target={d.id}
                                                    onClick={() => onArchiveDevice(d)}
                                                />
                                            )}
                                        </TableActionContainer>
                                    )}
                                />
                            )}
                        </DataTable>
                        <TablePagination data={organizationDevices} />
                    </Card>
                </ContentColumn>
            </LayoutRowContainer>
        </LayoutColumnContainer>
    )
}
